<script setup lang="ts">
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import { AppHeader } from '@/widgets/Header/index';
import { AppFooter } from '@/widgets/Footer/index';
import { AuthModal, prefetchAuthModal } from '@/widgets/AuthModal';
import { ContactsModal, prefetchContactsModal } from '@/widgets/ContactsModal';
import { CabinetTabBar } from '@/widgets/CabinetView';
import IconSprite from '@/shared/ui/IconSprite/IconSprite.vue';
import CookiesPanel, { useCookiesPanel } from '@/widgets/CookiesPanel';
import { useOpenedModals } from '@/shared/composables/useOpenedModals';
import SchemaOrg from '@/shared/ui/SchemaOrg/index';
import {
  organizationData,
  organizationShortData,
  webSiteData,
  webSiteShortData,
} from '@/app/lib/schemaConstants';

const route = useRoute();
const canonicalUrl = computed(() => `${ROOT_URL_HOST}${route.path}`);
const isMainPage: ComputedRef<boolean> = computed(() => route.path === '/');
const organizationSchema = computed(() =>
  isMainPage.value ? organizationData : organizationShortData,
);
const webSiteSchema = computed(() => (isMainPage.value ? webSiteData : webSiteShortData));

const platform = useRuntimeConfig().public.platform;
const isOpenedMenu: Ref<boolean> = ref(false);

const isMobile: Ref<boolean> = computed(() => platform === 'mobile');

const MobileMenu =
  __PLATFORM__ === 'mobile'
    ? defineAsyncComponent(() => import('@/widgets/Header/ui/mobile/MobileMenu.vue'))
    : null;

provide('isMobile', isMobile.value);
provide('isOpenedMenu', isOpenedMenu);

const { isShowCookiesPanel, acceptCookies } = useCookiesPanel();

const { openedModals } = useOpenedModals(['auth', 'contacts'], () => {
  prefetchAuthModal();
  prefetchContactsModal();
});

watch(
  () => route.fullPath,
  () => {
    isOpenedMenu.value = false;
  },
);

useHead({
  link: [{ rel: 'canonical', href: canonicalUrl, key: 'canonical' }],
});
</script>

<template>
  <div class="page-wrap">
    <SchemaOrg type="Organization" :data="organizationSchema" />
    <SchemaOrg type="WebSite" :data="webSiteSchema" />
    <AppHeader />
    <main id="main" class="page-wrap__main">
      <slot></slot>
      <MobileMenu v-if="isMobile && MobileMenu" />
    </main>
    <AppFooter />
    <CookiesPanel v-if="isShowCookiesPanel" @accept="acceptCookies" />
    <AuthModal v-if="openedModals.auth" />
    <ContactsModal v-if="openedModals.contacts" />
    <div id="teleports"></div>
    <CabinetTabBar v-if="isMobile" />
    <IconSprite />
  </div>
</template>

<style lang="less">
.page-wrap {
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 100dvh;

  &__main {
    flex: 1;
  }
}
</style>
